/**
 * Subscription Upgrade Modal Component (Web-Only)
 * Professional upgrade flow with plan comparison and payment integration
 * 
 * IMPORTANT: This modal is ONLY used by web platforms. Native platforms use
 * the NativePlanDisplay component in AccountPlan.js which must remain untouched.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useOrganization } from '../contexts/OrganizationContext';
import { useTrial } from '../contexts/TrialContext';
import PlatformButton from './PlatformButton';
import PlatformText from './PlatformText';
import PlatformCard from './PlatformCard';
import { formatPrice } from '../data/subscriptionPlans';
import SubscriptionService from '../services/SubscriptionService';
import PaymentService from '../services/PaymentService';
import './SubscriptionUpgradeModal.css';

const SubscriptionUpgradeModal = ({ 
  isOpen, 
  onClose, 
  selectedPlan, 
  onUpgradeComplete,
  showCurrentPlan = true 
}) => {
  const { t } = useTranslation('subscription');
  const { t: tCommon } = useTranslation('common');
  const { organizationId, organizationData, userRole } = useOrganization();
  const { hasActiveSubscription, trialStatus, fetchTrialStatus } = useTrial();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [billingInterval, setBillingInterval] = useState('month');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [upgradeData, setUpgradeData] = useState(null);
  
  // Track if modal was initialized to prevent reset during success step
  const hasInitialized = useRef(false);

  // Reset modal state when opened/closed
  // Only reset when modal FIRST opens, not when subscription status updates during success
  useEffect(() => {
    if (isOpen && !hasInitialized.current) {
      // Modal is opening for the first time - reset state
      setCurrentStep(1);
      setBillingInterval('month');
      setError(null);
      setUpgradeData(null);
      hasInitialized.current = true;
    } else if (!isOpen) {
      // Modal is closed - reset the initialization flag
      hasInitialized.current = false;
    }
  }, [isOpen]);

  // Get current plan info
  const getCurrentPlanInfo = () => {
    if (hasActiveSubscription && trialStatus?.subscriptionPlan) {
      return SubscriptionService.getPlan(trialStatus.subscriptionPlan);
    }
    return null;
  };

  // Calculate upgrade pricing
  const calculateUpgradePricing = () => {
    if (!selectedPlan) return null;

    const currentPlan = getCurrentPlanInfo();
    const currentPrice = currentPlan ? 
      (billingInterval === 'year' ? (currentPlan.yearlyPrice || 0) : (currentPlan.monthlyPrice || 0)) : 0;
    
    const newPrice = billingInterval === 'year' ? (selectedPlan.yearlyPrice || 0) : (selectedPlan.monthlyPrice || 0);
    const additionalCost = newPrice - currentPrice;
    const savings = billingInterval === 'year' ? (selectedPlan.yearlySavings || 0) : 0;

    return {
      currentPrice,
      newPrice,
      additionalCost: Math.max(0, additionalCost),
      savings,
      totalPrice: newPrice
    };
  };

  // Handle billing interval toggle
  const handleBillingToggle = (interval) => {
    setBillingInterval(interval);
  };

  // Handle upgrade initiation
  const handleInitiateUpgrade = async () => {
    if (userRole !== 'BusinessOwner') {
      setError(t('upgrade.errors.unauthorized'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const pricing = calculateUpgradePricing();
      const upgradeDataObj = {
        planId: selectedPlan.id,
        planName: selectedPlan.name,
        billingInterval,
        currentPlan: getCurrentPlanInfo(),
        pricing,
        organizationId: organizationId,
        userId: organizationData?.currentUser?.uid,
        initiatedAt: new Date().toISOString()
      };

      setUpgradeData(upgradeDataObj);
      setCurrentStep(2);
    } catch (err) {
      setError(t('upgrade.errors.initiationFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Handle payment processing with Razorpay (WEB-ONLY)
  const handlePaymentProcessing = useCallback(async () => {
    // Safety check: Ensure upgradeData exists
    if (!upgradeData) {
      setError('Upgrade data not available. Please try again.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Initialize PaymentService
      await PaymentService.initialize({
        razorpayKey: process.env.REACT_APP_RAZORPAY_KEY_ID
      });

      // 2. Create Razorpay order (STEP 1.1)
      const paymentIntent = await PaymentService.createPaymentIntent(upgradeData);

      // 3. Process payment with Razorpay Checkout (STEP 1.2)
      const paymentResult = await PaymentService.processPayment({
        orderId: paymentIntent.orderId,
        amount: upgradeData.pricing.totalPrice,
        description: `Subscription: ${upgradeData.planName}`,
        name: organizationData?.currentUser?.fullName || 'Customer',
        email: organizationData?.currentUser?.email || '',
        contact: organizationData?.currentUser?.phone || ''
      });

      // 4. Verify payment signature (STEP 1.5)
      await PaymentService.verifyPayment(paymentResult, {
        organizationId: upgradeData.organizationId,
        planId: upgradeData.planId,
        billingInterval: upgradeData.billingInterval,
        amount: upgradeData.pricing.totalPrice
      });

      // 5. Payment verified and subscription updated
      // Set success step FIRST to ensure it displays before any re-renders
      // This prevents the modal from resetting when subscription status updates
      setCurrentStep(3);
      setLoading(false); // CRITICAL: Reset loading state on success
      
      // Then refresh subscription/trial status (this will cause re-render, but step 3 is already set)
      // Do this AFTER setting step 3 to prevent timing issues
      try {
        await fetchTrialStatus();
      } catch (_) {
        // Ignore errors - subscription was already updated in backend
      }
      
    } catch (err) {
      if (err.message && err.message.includes('Payment cancelled')) {
        setError(t('upgrade.errors.paymentCancelled'));
      } else if (err.message && err.message.includes('not configured')) {
        setError('Payment gateway not configured. Please contact support.');
      } else {
        setError(t('upgrade.errors.paymentFailed') || 'Payment processing failed');
      }
      
      setLoading(false);
    }
  }, [upgradeData, organizationData, t]);

  // Handle upgrade completion
  const handleUpgradeComplete = () => {
    if (onUpgradeComplete) {
      onUpgradeComplete(upgradeData);
    }
    onClose();
  };

  // Render current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderPlanConfirmation();
      case 2:
        return renderPaymentProcessing();
      case 3:
        return renderSuccess();
      default:
        return null;
    }
  };

  // Render step 1: Plan Confirmation
  const renderPlanConfirmation = () => {
    const pricing = calculateUpgradePricing();
    const currentPlan = getCurrentPlanInfo();

    // Safety check - don't render if no plan selected
    if (!selectedPlan) {
      return (
        <div className="upgrade-step-content">
          <PlatformText variant="body" className="error-text">
            {t('upgrade.errors.noPlanSelected')}
          </PlatformText>
        </div>
      );
    }

    // Safety check - don't render if pricing calculation failed
    if (!pricing) {
      return (
        <div className="upgrade-step-content">
          <PlatformText variant="body" className="error-text">
            {t('upgrade.errors.pricingCalculationFailed')}
          </PlatformText>
        </div>
      );
    }

    return (
      <div className="upgrade-step-content">
        {/* Simplified Header */}
        <div className="upgrade-header-simple">
          <PlatformText variant="h3" className="upgrade-title-simple">
            Upgrade to {selectedPlan.name} Plan
          </PlatformText>
          <PlatformText variant="caption" className="upgrade-subtitle-simple">
            Review your billing and plan preferences
          </PlatformText>
        </div>

        {/* Current Plan - Simplified */}
        {showCurrentPlan && currentPlan && (
          <PlatformCard variant="outlined" className="current-plan-card-simple">
            <PlatformText variant="caption" className="current-plan-label">
              {t('upgrade.currentPlan')}
            </PlatformText>
            <div className="current-plan-info">
              <PlatformText variant="body" className="current-plan-name-simple">
                {currentPlan.name}
              </PlatformText>
              <PlatformText variant="caption" className="current-plan-price-simple">
                {formatPrice(billingInterval === 'year' ? currentPlan.yearlyPrice : currentPlan.monthlyPrice)}
                /{billingInterval === 'year' ? tCommon('year') : tCommon('month')}
              </PlatformText>
            </div>
          </PlatformCard>
        )}

        {/* Selected Plan - Light Card Style */}
        <PlatformCard variant="outlined" className="selected-plan-card-light">
          <div className="plan-header-light">
            <PlatformText variant="caption" className="upgrading-indicator">
              Upgrading to <strong>{selectedPlan.name} Plan</strong>
            </PlatformText>
          </div>

          {/* Pricing + Toggle Combined */}
          <div className="plan-pricing-compact">
            <div className="pricing-display-compact">
              <span className="plan-main-price-compact">
                {formatPrice(billingInterval === 'year' ? selectedPlan.yearlyPrice : selectedPlan.monthlyPrice)}
              </span>
              <span className="plan-period-compact">
                /{billingInterval === 'year' ? tCommon('year') : tCommon('month')}
              </span>
              {billingInterval === 'year' && selectedPlan.yearlySavings > 0 && (
                <span className="plan-savings-compact">
                  Save {formatPrice(selectedPlan.yearlySavings)}
                </span>
              )}
            </div>

            {/* Billing Toggle */}
            <div className="billing-toggle-compact">
              <button
                className={`toggle-btn-compact ${billingInterval === 'month' ? 'active' : ''}`}
                onClick={() => handleBillingToggle('month')}
              >
                {tCommon('monthly')}
              </button>
              <button
                className={`toggle-btn-compact ${billingInterval === 'year' ? 'active' : ''}`}
                onClick={() => handleBillingToggle('year')}
              >
                {tCommon('yearly')}
              </button>
            </div>
          </div>

          {/* Plan Description */}
          <PlatformText variant="body" className="plan-description-light">
            {selectedPlan.description}
          </PlatformText>
        </PlatformCard>

        {/* Action Buttons - Refined */}
        <div className="upgrade-actions-refined">
          <PlatformButton
            variant="outline"
            onClick={onClose}
            className="cancel-btn-refined"
          >
            {tCommon('cancel')}
          </PlatformButton>
          <PlatformButton
            variant="primary"
            onClick={handleInitiateUpgrade}
            disabled={loading}
            className="confirm-btn-refined"
          >
            {loading ? tCommon('loading') : t('upgrade.confirmUpgrade')}
          </PlatformButton>
        </div>
      </div>
    );
  };

  // Auto-trigger payment processing when step 2 loads
  useEffect(() => {
    if (currentStep === 2 && upgradeData && !loading && !error) {
      handlePaymentProcessing();
    }
  }, [currentStep, upgradeData, loading, error, handlePaymentProcessing]);

  // Render step 2: Payment Processing
  const renderPaymentProcessing = () => {
    return (
      <div className="upgrade-step-content">
        <div className="payment-processing">
          <div className="processing-spinner">
            <div className="spinner"></div>
          </div>
          <PlatformText variant="h3" className="processing-title">
            {t('upgrade.processingPayment')}
          </PlatformText>
          <PlatformText variant="body" className="processing-subtitle">
            {t('upgrade.processingSubtitle')}
          </PlatformText>
          
          {error && (
            <PlatformCard variant="outlined" className="error-card">
              <PlatformText variant="body" className="error-text">
                {error}
              </PlatformText>
            </PlatformCard>
          )}
        </div>
      </div>
    );
  };

  // Render step 3: Success
  const renderSuccess = () => {
    return (
      <div className="upgrade-step-content">
        <div className="success-content">
          <div className="success-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" 
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <PlatformText variant="h2" className="success-title">
            {t('upgrade.upgradeSuccessful')}
          </PlatformText>
          <PlatformText variant="body" className="success-subtitle">
            {t('upgrade.upgradeSuccessfulSubtitle', { planName: selectedPlan?.name || 'your plan' })}
          </PlatformText>
          
          <PlatformCard variant="outlined" className="success-details">
            <PlatformText variant="body" className="success-plan">
              {selectedPlan.name} - {billingInterval === 'year' ? tCommon('yearly') : tCommon('monthly')}
            </PlatformText>
            <PlatformText variant="small" className="success-price">
              {formatPrice(upgradeData?.pricing?.newPrice)}/{billingInterval === 'year' ? tCommon('year') : tCommon('month')}
            </PlatformText>
          </PlatformCard>

          <PlatformButton
            variant="primary"
            onClick={handleUpgradeComplete}
            className="complete-btn"
          >
            {t('upgrade.completeUpgrade')}
          </PlatformButton>
        </div>
      </div>
    );
  };

  // Don't render if modal is not open or no plan selected
  if (!isOpen || !selectedPlan) return null;

  // Don't render on native platforms (should not happen but safety check)
  // This is a web-only modal
  return (
    <div className="upgrade-modal-overlay web">
      <div className="upgrade-modal web">
        <div className="upgrade-modal-header">
          <PlatformText variant="h1" className="modal-title">
            {t('upgrade.upgradeSubscription')}
          </PlatformText>
          <PlatformButton
            variant="ghost"
            onClick={onClose}
            className="close-btn"
            disabled={loading}
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </PlatformButton>
        </div>

        <div className="upgrade-modal-body">
          {error && (
            <div className="error-card">
              <PlatformText variant="body" className="error-text">
                {error}
              </PlatformText>
            </div>
          )}
          {renderCurrentStep()}
        </div>

        {/* Progress Indicator - Shows step progress */}
          <div className="upgrade-progress">
            <div className={`progress-step ${currentStep >= 1 ? 'active' : ''}`}>
              <div className="step-number">1</div>
              <PlatformText variant="small" className="step-label">
                {t('upgrade.steps.confirm')}
              </PlatformText>
            </div>
            <div className={`progress-step ${currentStep >= 2 ? 'active' : ''}`}>
              <div className="step-number">2</div>
              <PlatformText variant="small" className="step-label">
                {t('upgrade.steps.payment')}
              </PlatformText>
            </div>
            <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>
              <div className="step-number">3</div>
              <PlatformText variant="small" className="step-label">
                {t('upgrade.steps.complete')}
              </PlatformText>
            </div>
          </div>
      </div>
    </div>
  );
};

export default SubscriptionUpgradeModal;
